import { ApplicationInterface } from '@lib/classes/API.interface';
import { ApplicationInterfaceFactory } from '@lib/classes/ApplicationInterface.factory';
import { ReturnOnly } from '@lib/classes/ReturnOnly';
import { uuid } from '@lib/random';
import { requiredProps } from '@lib/requiredProps';
import { isProduction } from '@lib/utils';
import { ModelClass } from '@model/Model';
import * as DataTypes from '@model/Types';
import { TypeInterface } from '@model/Types/Type';
import { castArray } from 'lodash';
import { ModelOptions } from './Model.interface';
import { _params } from './params';
import { TYPES } from './types';

const Types = {
    ...DataTypes,
};

const DEFAULTS = {};
const EXTRACT_FIELDS = /\((.*)\)/;

const createType = (type) => {
    if (!type[TYPES.TYPE]) {
        type[TYPES.TYPE] = uuid();
    }

    Types[type[TYPES.TYPE]] = type;

    return type;
};

const _mapTypes = (...types) => types.map((type) => createType(type)[TYPES.TYPE]);

const _concatFields = (fields$, types) =>
    fields$.reduce(
        ({ content, count }, field) => {
            return {
                content: `${content}${field || ''}${types[count] || ''}`,
                count: count + 1,
            };
        },
        {
            content: '',
            count: 0,
        },
    ).content;

function _getDefault(type: TypeInterface<any>): any {
    if (!type) {
        return '';
    }

    // noinspection JSUnresolvedVariable
    if (type && typeof type.getDefault === 'function') {
        // noinspection JSUnresolvedFunction
        return type.getDefault();
    }

    // noinspection JSUnresolvedVariable
    const types = type[TYPES.TYPES] || type.prototype[TYPES.TYPES];

    // noinspection JSUnresolvedVariable
    const isCollection = type[TYPES.COLLECTION] || type.prototype[TYPES.COLLECTION];

    if (isCollection) {
        return [];
    }

    if (types) {
        return Object.keys(types).reduce((bag, key) => {
            bag[key] = _getDefault(types[key]);
            return bag;
        }, {});
    }

    return '';
}

// noinspection SuspiciousTypeOfGuard
const _parser = (fields: any, modelName: string) =>
    fields
        .replace(/\n/g, '')
        .split(';')
        .map((field) => (typeof field === 'string' ? field : '').toString().trim())
        .filter((field) => field.length > 0)
        .reduce((bag, field) => {
            const [_name, _type] = field.split(':');
            const type$ = (_type || 'any').trim();
            const name$ = _name || '';
            const fieldsMatches = name$.match(EXTRACT_FIELDS) || [];
            const propFields = fieldsMatches[1] || '';
            const [name, alias] = name$.replace(EXTRACT_FIELDS, '').split('->');

            const item = {
                name,
                propFields,
                prop: name,
                type: Types[type$],
            };

            if (!item.type) {
                throw new Error(`Model '${modelName}' Field '${name}' has undefined type '${type$}'`);
            }

            bag.push(item);

            if (alias) {
                bag.push({ ...item, name: alias });
            }

            return bag;
        }, [] as any[])
        .reduce(
            (bag, { name, prop, type, propFields }) => {
                bag.types[prop] = type;
                bag.defaults[prop] = _getDefault(type);
                bag.fields[prop] = typeof type.$fields === 'function' ? type.$fields() : _params(propFields);

                bag.props[name] = {
                    enumerable: true,
                    configurable: true,
                    get() {
                        return this.get(prop);
                    },
                    set(value: any) {
                        this.set(prop, value);
                    },
                };

                return bag;
            },
            {
                props: {},
                fields: {},
                defaults: {},
                types: {},
            },
        );

/**
 *
 * @param name
 * @param options
 * @return {Function}
 * @constructor
 */
export function Model<T = Record<string, any>>(
    name: string,
    options: ModelOptions = {},
): (fields$: TemplateStringsArray, ...types: any[]) => T & ModelClass & ApplicationInterface<T> {
    return (fields$: TemplateStringsArray, ...types: any[]): T & ModelClass & ApplicationInterface<T> => {
        if (!isProduction()) {
            try {
                requiredProps(options, 'endpoint');
            } catch (e) {
                console.error(name, e);
            }
        }

        const PlainClass = class extends ModelClass {
            // ModelClass Body
        };

        const class$ = createType(ApplicationInterfaceFactory<ModelClass>(PlainClass as any, options.endpoint));

        fields$ = castArray(fields$) as any;
        types = _mapTypes(...types);

        const fields = _parser(_concatFields(fields$, types), name);

        fields.props[TYPES.NAME] = { value: name };
        fields.props[TYPES.TYPES] = { value: fields.types };
        fields.props[TYPES.FIELDS] = { value: fields.fields };
        fields.props[TYPES.DEFAULTS] = { value: fields.defaults };

        Object.defineProperties(class$.prototype, fields.props);

        if (typeof options.extend === 'function') {
            return options.extend(class$);
        }

        /**
         * Define class methods/props
         * In case of methods, first argument will be `this` (self) every time
         * @param key
         * @param value
         */
        class$.$define = (key: string, value: any) => {
            if (typeof value === 'function') {
                class$.prototype[key] = function (...args) {
                    return value(this, ...args);
                };
            } else {
                class$.prototype[key] = value;
            }

            return class$;
        };

        /**
         * Same as $define but for static methods
         * @param key
         * @param value
         * @returns {*}
         */
        class$.$static = (key: string, value: any) => {
            if (value) {
                if (typeof value === 'function') {
                    class$[key] = function (...args) {
                        return value(this, ...args);
                    };
                } else {
                    class$[key] = value;
                }

                return class$;
            }

            return class$[key];
        };

        /**
         * Check if a class it's same as class$
         * @param proto
         * @returns {boolean}
         */
        class$.$type = (proto: Record<string, any>) =>
            class$.isPrototypeOf(proto) || class$.prototype.isPrototypeOf(proto);

        /**
         * Check if an object it's instance of class$
         * @param instance
         * @returns {boolean}
         */
        class$.$instance = (instance: Record<string, any>) => instance instanceof class$;

        /**
         * Get return only (static method)
         * @returns {*}
         */
        class$.$fields = () => ReturnOnly.stringify(class$.prototype[TYPES.FIELDS]);

        /**
         * Get defaults props
         * @returns {*}
         */
        class$.$defaults = (deep: boolean): { [key: string]: any } => {
            const key = `defaults_${class$[TYPES.TYPE]}_${deep}`;
            if (!DEFAULTS[key]) {
                DEFAULTS[key] = new class$().$defaults(deep);
            }

            return DEFAULTS[key];
        };

        return class$;
    };
}
